import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addDoc,
  collection,
  documentId,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../components/UserContext";
import { logBitacora } from '../utils/bitacora';
import { mutateContract } from '../utils/contractWrites';

const DataContext = createContext({
  clientsById: {},
  clientsList: [],
  clientsLoading: true,
  clientsError: null,
  contractsById: {},
  contractsList: [],
  contractsLoading: true,
  contractsError: null,
  linksById: {},
  linksLoading: true,
  linksError: null,
  afianzadorasList: [],
  afianzadorasById: {},
  afianzadorasLoading: true,
  conveniosById: {},
  pendientes: [],
  pendientesLoading: true,
  pendientesError: null,
  otEnabled: false,
  ordenesTrabajo: [],
  ordenesTrabajoLoading: true,
  ordenesTrabajoError: null,
  conveniosList: [],
  conveniosLoading: true,
  conveniosError: null,
  addClient: async () => {},
  updateClient: async () => {},
  updateClientAssignedUsers: async () => {},
  updateContractAssignedUsers: async () => {},
  addConvenio: async () => {},
  updateConvenio: async () => {},
});

const chunkArray = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

// Archivado = borrado lógico. An archived contract never enters the context and
// an archived fianza is stripped from fianzas[] before anyone reads it, so every
// list, total, Pulso, Pendiente, report and the client portal see live records
// only. Safe because every fianzas[] write goes through the transactions in
// utils/contractWrites (they re-read the document; the stripped array is never
// written back). Archived records load on demand through useArchive().
const stripArchived = (contractDoc, extra = {}) => {
  const data = contractDoc.data() || {};
  if (data.archived === true) return null;
  const fianzas = Array.isArray(data.fianzas) ? data.fianzas : [];
  const vivas = fianzas.filter((f) => !f?.archivada);
  return {
    id: contractDoc.id,
    ...extra,
    ...data,
    fianzas: vivas,
    fianzasArchivadasCount: fianzas.length - vivas.length,
  };
};

export const DataProvider = ({ children }) => {
  const { user, userData, orgData } = useUser();
  const asociadosEnabled = Boolean(orgData?.asociadosEnabled);
  // Órdenes de Trabajo: an add-on Surelia switches on per org (orgs.otEnabled).
  const otEnabled = Boolean(orgData?.otEnabled);
  const [clientsById, setClientsById] = useState({});
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState(null);

  const [contractsById, setContractsById] = useState({});
  const [contractsLoading, setContractsLoading] = useState(true);
  const [contractsError, setContractsError] = useState(null);

  const [tarifas, setTarifas] = useState([]);
  const [tarifasLoading, setTarifasLoading] = useState(true);
  const [tarifasError, setTarifasError] = useState(null);
  const [comisiones, setComisiones] = useState([]);
  const [comisionesLoading, setComisionesLoading] = useState(true);
  const [comisionesError, setComisionesError] = useState(null);
  const [comisionConfig, setComisionConfig] = useState({});
  const [comisionConfigLoading, setComisionConfigLoading] = useState(true);
  const [asociados, setAsociados] = useState([]);
  const [asociadosLoading, setAsociadosLoading] = useState(true);
  const [aprobacionConfig, setAprobacionConfig] = useState({});
  const [propuestasReparto, setPropuestasReparto] = useState([]);

  const [linksById, setLinksById] = useState({});
  const [linksLoading, setLinksLoading] = useState(true);
  const [linksError, setLinksError] = useState(null);

  const [afianzadorasList, setAfianzadorasList] = useState([]);
  const [afianzadorasLoading, setAfianzadorasLoading] = useState(true);

  const [conveniosById, setConveniosById] = useState({});
  const [pendientes, setPendientes] = useState([]);
  const [pendientesLoading, setPendientesLoading] = useState(true);
  const [pendientesError, setPendientesError] = useState(null);
  const [ordenesTrabajo, setOrdenesTrabajo] = useState([]);
  const [ordenesTrabajoLoading, setOrdenesTrabajoLoading] = useState(true);
  const [ordenesTrabajoError, setOrdenesTrabajoError] = useState(null);
  const [conveniosLoading, setConveniosLoading] = useState(true);
  const [conveniosError, setConveniosError] = useState(null);

  const clientsUnsubscribeRef = useRef(null);
  const conveniosUnsubscribeRef = useRef(null);
  const contractListenersRef = useRef({});
  const contractIdsRef = useRef(new Set());
  const contractClientMapRef = useRef({});
  const linkListenersRef = useRef({});
  const linkIdsRef = useRef(new Set());

  // Separate listener refs for convenio contracts (different Firestore rule path)
  const convenioContractListenersRef = useRef({});
  const convenioContractIdsRef = useRef(new Set());

  const teardownContractListeners = useCallback(() => {
    Object.values(contractListenersRef.current).forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (err) {
        console.error("Error cleaning contract listener", err);
      }
    });
    contractListenersRef.current = {};
    contractIdsRef.current = new Set();
  }, []);

  const teardownLinkListeners = useCallback(() => {
    Object.values(linkListenersRef.current).forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (err) {
        console.error("Error cleaning link listener", err);
      }
    });
    linkListenersRef.current = {};
    linkIdsRef.current = new Set();
  }, []);

  // Sync link listeners based on link IDs from clients
  const syncLinkListeners = useCallback((linkIdsSet) => {
    const newIds = Array.from(linkIdsSet).filter(Boolean).sort();
    const newChunks = chunkArray(newIds, 7);
    const newChunkKeys = new Set(newChunks.map((chunk) => chunk.join("|")));

    // Remove listeners for chunks we no longer need
    Object.entries(linkListenersRef.current).forEach(([key, unsubscribe]) => {
      if (!newChunkKeys.has(key)) {
        try {
          unsubscribe();
        } catch (err) {
          console.error("Error unsubscribing link chunk", err);
        }
        delete linkListenersRef.current[key];
      }
    });

    // Remove links that are no longer referenced
    const toRemove = Array.from(linkIdsRef.current).filter(
      (id) => !linkIdsSet.has(id)
    );
    if (toRemove.length > 0) {
      setLinksById((prev) => {
        const next = { ...prev };
        toRemove.forEach((id) => delete next[id]);
        return next;
      });
    }

    linkIdsRef.current = linkIdsSet;

    // No links to listen to
    if (newChunks.length === 0) {
      setLinksLoading(false);
      return;
    }

    // Add listeners for new chunks
    newChunks.forEach((chunk) => {
      const key = chunk.join("|");
      if (linkListenersRef.current[key]) return;

      console.log("[Links] Setting up listener for chunk:", chunk);

      const linksQuery = query(
        collection(db, "links"),
        where(documentId(), "in", chunk)
      );

      const unsubscribe = onSnapshot(
        linksQuery,
        async (snapshot) => {
          console.log("[Links] Snapshot received for chunk:", chunk, "docs:", snapshot.docs.map(d => d.id));
          setLinksLoading(false);
          setLinksError(null);

          // Process each link and fetch its files subcollection
          const linkUpdates = {};

          for (const linkDoc of snapshot.docs) {
            const linkData = { id: linkDoc.id, ...linkDoc.data() };

            // Fetch files subcollection for this link
            try {
              const filesSnapshot = await getDocs(
                collection(db, "links", linkDoc.id, "files")
              );
              const filesData = {};
              filesSnapshot.forEach((fileDoc) => {
                filesData[fileDoc.id] = { id: fileDoc.id, ...fileDoc.data() };
              });
              linkData.filesData = filesData;
            } catch (err) {
              console.error("Error fetching link files:", err);
              linkData.filesData = {};
            }

            linkUpdates[linkDoc.id] = linkData;
          }

          setLinksById((prev) => {
            const next = { ...prev };
            // Clear entries for this chunk before applying updates
            chunk.forEach((id) => delete next[id]);
            Object.assign(next, linkUpdates);
            return next;
          });
        },
        (error) => {
          console.error("[Links] Listener FAILED for chunk:", chunk, "error:", error.code, error.message);
          setLinksError(error);
          setLinksLoading(false);
        }
      );

      linkListenersRef.current[key] = unsubscribe;
    });
  }, []);

  const syncContractListeners = useCallback((contractIdsSet) => {
    const newIds = Array.from(contractIdsSet).filter(Boolean).sort();
    const newChunks = chunkArray(newIds, 10);
    const newChunkKeys = new Set(newChunks.map((chunk) => chunk.join("|")));

    // Remove listeners for chunks we no longer need
    Object.entries(contractListenersRef.current).forEach(([key, unsubscribe]) => {
      if (!newChunkKeys.has(key)) {
        try {
          unsubscribe();
        } catch (err) {
          console.error("Error unsubscribing contract chunk", err);
        }
        delete contractListenersRef.current[key];
      }
    });

    // Remove contracts that are no longer referenced
    const toRemove = Array.from(contractIdsRef.current).filter(
      (id) => !contractIdsSet.has(id)
    );
    if (toRemove.length > 0) {
      setContractsById((prev) => {
        const next = { ...prev };
        toRemove.forEach((id) => delete next[id]);
        return next;
      });
    }

    contractIdsRef.current = contractIdsSet;

    // No contracts to listen to
    if (newChunks.length === 0) {
      setContractsLoading(false);
      return;
    }

    // Add listeners for new chunks
    newChunks.forEach((chunk) => {
      const key = chunk.join("|");
      if (contractListenersRef.current[key]) return;

      const contractsQuery = query(
        collection(db, "contracts"),
        where(documentId(), "in", chunk)
      );

      const unsubscribe = onSnapshot(
        contractsQuery,
        (snapshot) => {
          setContractsLoading(false);
          setContractsError(null);
          setContractsById((prev) => {
            const next = { ...prev };
            // Clear entries for this chunk before applying updates
            chunk.forEach((id) => delete next[id]);
            snapshot.forEach((contractDoc) => {
              const live = stripArchived(contractDoc, {
                clientId: contractClientMapRef.current[contractDoc.id],
              });
              if (live) next[contractDoc.id] = live;
            });
            return next;
          });
        },
        (error) => {
          console.error("Contracts listener error:", error);
          setContractsError(error);
          setContractsLoading(false);
        }
      );

      contractListenersRef.current[key] = unsubscribe;
    });
  }, []);

  // Separate sync for convenio contracts (different Firestore rule path)
  const syncConvenioContractListeners = useCallback((contractIdsSet) => {
    const newIds = Array.from(contractIdsSet).filter(Boolean).sort();
    const newChunks = chunkArray(newIds, 10);
    const newChunkKeys = new Set(newChunks.map((chunk) => chunk.join("|")));

    // Remove old listeners
    Object.entries(convenioContractListenersRef.current).forEach(([key, unsubscribe]) => {
      if (!newChunkKeys.has(key)) {
        try { unsubscribe(); } catch (err) { console.error("Error unsubscribing convenio contract chunk", err); }
        delete convenioContractListenersRef.current[key];
      }
    });

    // Remove contracts no longer referenced
    const oldIds = convenioContractIdsRef.current;
    const toRemove = Array.from(oldIds).filter((id) => !contractIdsSet.has(id));
    if (toRemove.length > 0) {
      setContractsById((prev) => {
        const next = { ...prev };
        toRemove.forEach((id) => delete next[id]);
        return next;
      });
    }

    convenioContractIdsRef.current = contractIdsSet;

    if (newChunks.length === 0) {
      setContractsLoading(false);
      return;
    }

    newChunks.forEach((chunk) => {
      const key = chunk.join("|");
      if (convenioContractListenersRef.current[key]) return;

      const contractsQuery = query(
        collection(db, "contracts"),
        where(documentId(), "in", chunk)
      );

      const unsubscribe = onSnapshot(
        contractsQuery,
        (snapshot) => {
          setContractsLoading(false);
          setContractsError(null);
          setContractsById((prev) => {
            const next = { ...prev };
            chunk.forEach((id) => delete next[id]);
            snapshot.forEach((contractDoc) => {
              const live = stripArchived(contractDoc);
              if (live) next[contractDoc.id] = live;
            });
            return next;
          });
        },
        (error) => {
          console.error("Convenio contracts listener error:", error);
          setContractsError(error);
          setContractsLoading(false);
        }
      );

      convenioContractListenersRef.current[key] = unsubscribe;
    });
  }, []);

  const teardownConvenioContractListeners = useCallback(() => {
    Object.values(convenioContractListenersRef.current).forEach((unsubscribe) => {
      try { unsubscribe(); } catch (err) { console.error("Error cleaning convenio contract listener", err); }
    });
    convenioContractListenersRef.current = {};
    convenioContractIdsRef.current = new Set();
  }, []);

  useEffect(() => {
    // Skip tarifas for client users
    if (userData?.rol === 'client') {
      setTarifas([]);
      setTarifasLoading(false);
      return;
    }

    const fetchTarifas = async () => {
      setTarifasLoading(true);
      setTarifasError(null);
      try {
        const collected = [];

        // Org-based: query all tarifas for the org
        if (userData?.org) {
          const orgQuery = query(
            collection(db, "tarifas"),
            where("org", "==", userData.org)
          );
          const snapshot = await getDocs(orgQuery);
          snapshot.forEach((d) => {
            collected.push({ id: d.id, ...d.data() });
          });
        } else {
          // Legacy: query by user's tarifas array
          const ids = userData?.tarifas || [];
          if (ids.length === 0) {
            setTarifas([]);
            setTarifasLoading(false);
            return;
          }
          const chunks = chunkArray(ids, 10);
          for (const chunk of chunks) {
            const tarifasQuery = query(
              collection(db, "tarifas"),
              where(documentId(), "in", chunk)
            );
            const snapshot = await getDocs(tarifasQuery);
            snapshot.forEach((d) => {
              collected.push({ id: d.id, ...d.data() });
            });
          }
        }

        setTarifas(collected);
      } catch (error) {
        console.error("Tarifas fetch error:", error);
        setTarifasError(error);
      } finally {
        setTarifasLoading(false);
      }
    };

    fetchTarifas();
  }, [user, userData?.tarifas, userData?.org, userData?.rol]);

  // ====================== COMISIONES LEDGER SUBSCRIPTION =======================
  // ONE org-scoped live listener for the comisión ledger (orgs/{org}/comisiones),
  // preloaded for the whole session. Any broker in the org (owner + operator)
  // can read it; client users get an empty list and no listener. This must match
  // the Firestore rule: when the ledger is unreadable the reportes silently fall
  // back to an on-the-fly comisión, so a narrower gate here would make the same
  // report show different numbers per role. Centralized — not per component — so
  // there is a single long-lived listener instead of many short-lived ones that
  // churn the Firestore watch stream ("INTERNAL ASSERTION FAILED: Unexpected
  // state"). Consumers (reportes, the fianza settlement panel) filter locally.
  useEffect(() => {
    if (!userData?.org || !['owner', 'operator'].includes(userData?.rol)) {
      setComisiones([]);
      setComisionesLoading(false);
      return undefined;
    }
    setComisionesLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, 'orgs', userData.org, 'comisiones'),
      (snapshot) => {
        // Movements of an archived fianza carry `archivada` and stop counting
        // everywhere, same as the fianza. They stay in Firestore for the trail.
        setComisiones(
          snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((m) => !m.archivada)
        );
        setComisionesError(null);
        setComisionesLoading(false);
      },
      (error) => {
        console.error("Comisiones listener error:", error);
        setComisionesError(error);
        setComisionesLoading(false);
      }
    );
    return unsubscribe;
  }, [userData?.org, userData?.rol]);

  // ====================== COMISION CONFIG SUBSCRIPTION =======================
  // orgs/{org}/config/comisiones — participación de convenios per afianzadora.
  // Broker-readable (rules mirror the ledger gate: dialogs and reportes need it
  // to preview net comisiones); only the owner writes it.
  useEffect(() => {
    if (!userData?.org || !['owner', 'operator'].includes(userData?.rol)) {
      setComisionConfig({});
      setComisionConfigLoading(false);
      return undefined;
    }
    setComisionConfigLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'orgs', userData.org, 'config', 'comisiones'),
      (snapshot) => {
        setComisionConfig(snapshot.exists() ? snapshot.data() : {});
        setComisionConfigLoading(false);
      },
      (error) => {
        console.error('Comision config listener error:', error);
        setComisionConfig({});
        setComisionConfigLoading(false);
      }
    );
    return unsubscribe;
  }, [userData?.org, userData?.rol]);

  // ====================== ASOCIADOS SUBSCRIPTION =======================
  // orgs/{org}/asociados — who shares the comisión (asociados and socios).
  // Only when Surelia switched the module on for the org (orgs.asociadosEnabled);
  // brokers read, only the owner writes (rules). Ledger rows carry their own
  // name snapshot, so history renders without this list.
  useEffect(() => {
    if (!asociadosEnabled || !userData?.org || !['owner', 'operator'].includes(userData?.rol)) {
      setAsociados([]);
      setAsociadosLoading(false);
      return undefined;
    }
    setAsociadosLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, 'orgs', userData.org, 'asociados'),
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
        setAsociados(list);
        setAsociadosLoading(false);
      },
      (error) => {
        console.error('Asociados listener error:', error);
        setAsociados([]);
        setAsociadosLoading(false);
      }
    );
    return unsubscribe;
  }, [asociadosEnabled, userData?.org, userData?.rol]);

  // ====================== APROBACIÓN DE REPARTO =======================
  // config/asociados (requiereAprobacion, aprobadores, regla) and the pending
  // propuestas. Owner-only: propuestas are owner-read by rules, and the whole
  // approval surface is the owner's.
  useEffect(() => {
    if (!asociadosEnabled || !userData?.org || userData?.rol !== 'owner') {
      setAprobacionConfig({});
      setPropuestasReparto([]);
      return undefined;
    }
    const unsubConfig = onSnapshot(
      doc(db, 'orgs', userData.org, 'config', 'asociados'),
      (snap) => setAprobacionConfig(snap.exists() ? snap.data() : {}),
      (error) => { console.error('Config asociados listener error:', error); setAprobacionConfig({}); }
    );
    const unsubProps = onSnapshot(
      query(collection(db, 'orgs', userData.org, 'reparto_propuestas'), where('estado', '==', 'pendiente')),
      (snap) => setPropuestasReparto(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (error) => { console.error('Propuestas reparto listener error:', error); setPropuestasReparto([]); }
    );
    return () => { unsubConfig(); unsubProps(); };
  }, [asociadosEnabled, userData?.org, userData?.rol]);

  // ====================== PENDIENTES SUBSCRIPTION =======================
  // ONE org-scoped live listener for `pendientes`, for the same reason the
  // comisión ledger has one: the Pendientes page, the Pulso tab and every
  // PendientesPanel (ClienteDetails tab, inside FianzaView) read this at the
  // same time, and panels mount/unmount with their modals. A per-component
  // onSnapshot meant several identical listeners on the same query churning the
  // watch stream, which trips "INTERNAL ASSERTION FAILED: Unexpected state" in
  // the Firestore SDK. Centralized here, DataProvider outlives every consumer
  // so the listener is created once per session.
  //
  // Brokers only. Pendientes are internal to the team: client users have no
  // read path at all (no rules branch, no portal page).
  useEffect(() => {
    if (!userData?.org || !['owner', 'operator'].includes(userData?.rol)) {
      setPendientes([]);
      setPendientesLoading(false);
      return undefined;
    }
    setPendientesLoading(true);
    const unsubscribe = onSnapshot(
      query(collection(db, 'pendientes'), where('org', '==', userData.org)),
      (snapshot) => {
        setPendientes(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPendientesError(null);
        setPendientesLoading(false);
      },
      (error) => {
        console.error('Pendientes listener error:', error);
        setPendientesError(error);
        setPendientesLoading(false);
      }
    );
    return unsubscribe;
  }, [userData?.org, userData?.rol]);

  // ====================== ÓRDENES DE TRABAJO SUBSCRIPTION =======================
  // ONE org-scoped live listener for `ordenes_trabajo`, same reasoning as
  // pendientes: the page, the bell, the Carga and Estadísticas views and each
  // open OtView read it at once. Only when Surelia switched the add-on on for
  // the org (orgs.otEnabled) and only for brokers — OTs are internal to the
  // team. Closed OTs stay in the query: the historial and the stats need them.
  useEffect(() => {
    if (!otEnabled || !userData?.org || !['owner', 'operator'].includes(userData?.rol)) {
      setOrdenesTrabajo([]);
      setOrdenesTrabajoLoading(false);
      return undefined;
    }
    setOrdenesTrabajoLoading(true);
    const unsubscribe = onSnapshot(
      query(collection(db, 'ordenes_trabajo'), where('org', '==', userData.org)),
      (snapshot) => {
        setOrdenesTrabajo(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setOrdenesTrabajoError(null);
        setOrdenesTrabajoLoading(false);
      },
      (error) => {
        console.error('Ordenes de trabajo listener error:', error);
        setOrdenesTrabajoError(error);
        setOrdenesTrabajoLoading(false);
      }
    );
    return unsubscribe;
  }, [otEnabled, userData?.org, userData?.rol]);

  // Owner-only by rules; merge so future config keys coexist.
  const saveComisionConfig = useCallback(async (updates) => {
    if (!userData?.org) throw new Error('Sin organización');
    return setDoc(doc(db, 'orgs', userData.org, 'config', 'comisiones'),
      { ...updates, updatedAt: serverTimestamp() }, { merge: true });
  }, [userData?.org]);

  // Fetch global afianzadoras catalog (once, rarely changes)
  useEffect(() => {
    const fetchAfianzadoras = async () => {
      try {
        const q = query(collection(db, 'afianzadoras'), where('pais', '==', 'MX'));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        setAfianzadorasList(list);
      } catch (err) {
        console.error('Error fetching global afianzadoras:', err);
      } finally {
        setAfianzadorasLoading(false);
      }
    };
    fetchAfianzadoras();
  }, []);

  useEffect(() => {
    // Reset state on user change
    setClientsById({});
    setClientsLoading(true);
    setClientsError(null);
    setContractsById({});
    setContractsLoading(true);
    setContractsError(null);
    setLinksById({});
    setLinksLoading(true);
    setLinksError(null);
    contractClientMapRef.current = {};

    if (clientsUnsubscribeRef.current) {
      clientsUnsubscribeRef.current();
    }
    teardownContractListeners();
    teardownLinkListeners();

    if (!user?.uid || !userData) {
      setClientsLoading(false);
      setContractsLoading(false);
      setLinksLoading(false);
      return undefined;
    }

    // Build query based on role, org membership, or legacy usuario[] array
    let clientesQuery;
    let isClientUser = userData.rol === 'client';

    if (isClientUser && userData?.clientIds?.length > 0) {
      // CLIENT USER: Fetch only assigned clients by document ID (chunks of 10)
      const clientIdChunks = chunkArray(userData.clientIds, 10);
      const allUnsubscribes = [];

      clientIdChunks.forEach((chunk) => {
        const q = query(
          collection(db, "clientes"),
          where(documentId(), "in", chunk)
        );
        const unsub = onSnapshot(
          q,
          (snapshot) => {
            setClientsLoading(false);
            setClientsError(null);

            const nextClients = {};
            const contractIds = new Set();
            const contractClientMap = {};
            const linkIds = new Set();

            snapshot.forEach((clienteDoc) => {
              const data = clienteDoc.data();
              if (data.archived === true) return;
              nextClients[clienteDoc.id] = { id: clienteDoc.id, ...data };

              if (Array.isArray(data.contracts)) {
                data.contracts.forEach((contractId) => {
                  if (!contractId) return;
                  contractIds.add(contractId);
                  if (!contractClientMap[contractId]) {
                    contractClientMap[contractId] = clienteDoc.id;
                  }
                });
              }

              if (Array.isArray(data.links)) {
                data.links.forEach((linkId) => {
                  if (linkId) linkIds.add(linkId);
                });
              }
            });

            contractClientMapRef.current = { ...contractClientMapRef.current, ...contractClientMap };
            setClientsById((prev) => ({ ...prev, ...nextClients }));
            syncContractListeners(contractIds);
            syncLinkListeners(linkIds);
          },
          (error) => {
            console.error("Client user clients listener error:", error);
            setClientsError(error);
            setClientsLoading(false);
          }
        );
        allUnsubscribes.push(unsub);
      });

      clientsUnsubscribeRef.current = () => allUnsubscribes.forEach((u) => u());

      return () => {
        allUnsubscribes.forEach((u) => u());
        teardownContractListeners();
        teardownLinkListeners();
      };
    } else if (isClientUser) {
      // Client user with 0 clientIds — empty state for clients
      setClientsLoading(false);
      setLinksLoading(false);
      // Only set contractsLoading false if user has no convenio access either
      // (convenio contracts will be synced by the convenios effect)
      if (!userData?.convenioAccess || Object.keys(userData.convenioAccess).length === 0) {
        setContractsLoading(false);
      }
      return undefined;
    }

    if (userData?.org) {
      // ORG: Org-based access - query all org clients
      clientesQuery = query(
        collection(db, "clientes"),
        where("org", "==", userData.org),
        orderBy("nombreEmpresa")
      );
    } else {
      // LEGACY: Fallback for unmigrated users using usuario[] array
      clientesQuery = query(
        collection(db, "clientes"),
        where("usuario", "array-contains", user.uid),
        orderBy("nombreEmpresa")
      );
    }

    const unsubscribe = onSnapshot(
      clientesQuery,
      (snapshot) => {
        setClientsLoading(false);
        setClientsError(null);

        const nextClients = {};
        const contractIds = new Set();
        const contractClientMap = {};
        const linkIds = new Set();

        snapshot.forEach((clienteDoc) => {
          const data = clienteDoc.data();
          // Skip archived clients — they are fetched on demand
          if (data.archived === true) return;
          // All org members see all org clients (no operator filtering)
          nextClients[clienteDoc.id] = { id: clienteDoc.id, ...data };

          if (Array.isArray(data.contracts)) {
            data.contracts.forEach((contractId) => {
              if (!contractId) return;
              contractIds.add(contractId);
              if (!contractClientMap[contractId]) {
                contractClientMap[contractId] = clienteDoc.id;
              }
            });
          }

          // Collect link IDs for preloading
          if (Array.isArray(data.links)) {
            data.links.forEach((linkId) => {
              if (linkId) linkIds.add(linkId);
            });
          }
        });

        contractClientMapRef.current = contractClientMap;
        setClientsById(nextClients);
        syncContractListeners(contractIds);
        syncLinkListeners(linkIds);
      },
      (error) => {
        console.error("Clients listener error:", error);
        setClientsError(error);
        setClientsLoading(false);
      }
    );

    clientsUnsubscribeRef.current = unsubscribe;

    return () => {
      unsubscribe();
      teardownContractListeners();
      teardownLinkListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, userData?.org, userData?.rol, userData?.clientIds, syncContractListeners, teardownContractListeners, syncLinkListeners, teardownLinkListeners]);

  // ====================== CONVENIOS SUBSCRIPTION =======================

  useEffect(() => {
    setConveniosById({});
    setConveniosLoading(true);
    setConveniosError(null);
    teardownConvenioContractListeners();

    if (conveniosUnsubscribeRef.current) {
      conveniosUnsubscribeRef.current();
    }

    if (!user?.uid || !userData) {
      setConveniosLoading(false);
      return undefined;
    }

    const isClient = userData.rol === 'client';

    if (isClient) {
      // Client users: fetch convenios from their convenioAccess map keys
      const convenioAccess = userData.convenioAccess;
      if (!convenioAccess || Object.keys(convenioAccess).length === 0) {
        setConveniosLoading(false);
        return undefined;
      }

      const convenioIds = Object.keys(convenioAccess);
      const allUnsubscribes = [];
      // Track contract IDs the client path is currently listening to, by chunk key.
      // Lets us tear down listeners when contracts leave a convenio.
      const clientContractIdsByChunk = {};

      const reconcileClientContractListeners = () => {
        // Union of all currently-referenced contract IDs across chunks
        const union = new Set();
        Object.values(clientContractIdsByChunk).forEach((set) => {
          set.forEach((id) => union.add(id));
        });

        // Tear down listeners for contracts that left every convenio
        Object.entries(convenioContractListenersRef.current).forEach(([key, unsub]) => {
          if (!key.startsWith('conv_client_')) return;
          const contractId = key.slice('conv_client_'.length);
          if (!union.has(contractId)) {
            try { unsub(); } catch (err) { console.error("Error tearing down convenio contract listener", err); }
            delete convenioContractListenersRef.current[key];
            setContractsById((prev) => {
              if (!(contractId in prev)) return prev;
              const next = { ...prev };
              delete next[contractId];
              return next;
            });
          }
        });

        // Bring up listeners for new contracts
        union.forEach((contractId) => {
          const listenerKey = `conv_client_${contractId}`;
          if (convenioContractListenersRef.current[listenerKey]) return;

          const contractUnsub = onSnapshot(
            doc(db, "contracts", contractId),
            (contractDoc) => {
              setContractsLoading(false);
              if (contractDoc.exists()) {
                setContractsById((prev) => ({
                  ...prev,
                  [contractDoc.id]: { id: contractDoc.id, ...contractDoc.data() },
                }));
              }
            },
            (error) => {
              console.error(`Client convenio contract listener error (${contractId}):`, error);
              setContractsLoading(false);
            }
          );
          convenioContractListenersRef.current[listenerKey] = contractUnsub;
        });

        if (union.size === 0) {
          setContractsLoading(false);
        }
      };

      // Single listener per chunk — populates conveniosById AND drives contract listeners
      const convenioChunks = chunkArray(convenioIds, 10);
      convenioChunks.forEach((chunk) => {
        const chunkKey = chunk.join('|');
        const unsub = onSnapshot(
          query(collection(db, "convenios"), where(documentId(), "in", chunk)),
          (snapshot) => {
            setConveniosLoading(false);
            setConveniosError(null);

            const nextConvenios = {};
            const contractIds = new Set();
            snapshot.forEach((convenioDoc) => {
              const data = convenioDoc.data();
              nextConvenios[convenioDoc.id] = { id: convenioDoc.id, ...data };
              if (Array.isArray(data.contracts)) {
                data.contracts.forEach((cid) => { if (cid) contractIds.add(cid); });
              }
            });
            setConveniosById((prev) => ({ ...prev, ...nextConvenios }));

            clientContractIdsByChunk[chunkKey] = contractIds;
            reconcileClientContractListeners();
          },
          (error) => {
            console.error("Client convenios listener error:", error);
            setConveniosError(error);
            setConveniosLoading(false);
          }
        );
        allUnsubscribes.push(unsub);
      });

      conveniosUnsubscribeRef.current = () => allUnsubscribes.forEach((u) => u());

      return () => {
        allUnsubscribes.forEach((u) => u());
        teardownConvenioContractListeners();
      };
    }

    // Broker users: fetch all org convenios
    if (!userData.org) {
      setConveniosLoading(false);
      return undefined;
    }

    const conveniosQuery = query(
      collection(db, "convenios"),
      where("org", "==", userData.org),
      orderBy("nombre")
    );

    const unsubscribe = onSnapshot(
      conveniosQuery,
      (snapshot) => {
        setConveniosLoading(false);
        setConveniosError(null);

        const nextConvenios = {};
        const contractIds = new Set();

        snapshot.forEach((convenioDoc) => {
          const data = convenioDoc.data();
          nextConvenios[convenioDoc.id] = { id: convenioDoc.id, ...data };

          if (Array.isArray(data.contracts)) {
            data.contracts.forEach((contractId) => {
              if (contractId) contractIds.add(contractId);
            });
          }
        });

        setConveniosById(nextConvenios);
        syncConvenioContractListeners(contractIds);
      },
      (error) => {
        console.error("Convenios listener error:", error);
        setConveniosError(error);
        setConveniosLoading(false);
      }
    );

    conveniosUnsubscribeRef.current = unsubscribe;

    return () => {
      unsubscribe();
      teardownConvenioContractListeners();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, userData?.org, userData?.rol, userData?.convenioAccess, syncConvenioContractListeners, teardownConvenioContractListeners]);

  // ====================== CONVENIO CRUD =======================

  const addConvenio = useCallback(
    async (data) => {
      if (!user?.uid) throw new Error("User not authenticated");

      const docRef = await addDoc(collection(db, "convenios"), {
        ...data,
        org: userData.org,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        estatus: "activo",
        beneficiarios: data.beneficiarios || [],
        proyectos: data.proyectos || [],
        contracts: [],
      });

      try {
        await logBitacora({
          action: 'CREATE',
          collection: 'convenios',
          documentId: docRef.id,
          metadata: {
            convenioId: docRef.id,
            convenioName: data.nombre || 'Sin nombre',
            description: `Creó nuevo convenio '${data.nombre || 'Sin nombre'}'`,
          },
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log convenio creation to bitacora:', logError);
      }

      return docRef;
    },
    [user, userData]
  );

  const updateConvenio = useCallback(
    async (convenioId, updates) => {
      return updateDoc(doc(db, "convenios", convenioId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    },
    []
  );

  const addClient = useCallback(
    async (data) => {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      // Create client
      const docRef = await addDoc(collection(db, "clientes"), data);

      // Log to bitacora (non-blocking)
      try {
        await logBitacora({
          action: 'CREATE',
          collection: 'clientes',
          documentId: docRef.id,
          metadata: {
            clientId: docRef.id,
            clientName: data.nombreEmpresa || 'Sin nombre',
            description: `Creó nuevo cliente '${data.nombreEmpresa || 'Sin nombre'}'`,
          },
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log client creation to bitacora:', logError);
      }

      return docRef;
    },
    [user, userData]
  );

  const updateClient = useCallback(async (clientId, updates) => {
    return updateDoc(doc(db, "clientes", clientId), updates);
  }, []);

  const updateClientAssignedUsers = useCallback(async (clientId, assignedUsers) => {
    return updateDoc(doc(db, "clientes", clientId), { assignedUsers });
  }, []);

  const updateContractAssignedUsers = useCallback(async (contractId, assignedUsers) => {
    return updateDoc(doc(db, "contracts", contractId), { assignedUsers });
  }, []);

  const archiveClient = useCallback(async (clientId) => {
    if (!user?.uid) throw new Error("User not authenticated");
    await updateDoc(doc(db, "clientes", clientId), {
      archived: true,
      archivedAt: serverTimestamp(),
      archivedBy: user.uid,
    });
  }, [user]);

  const unarchiveClient = useCallback(async (clientId) => {
    if (!user?.uid) throw new Error("User not authenticated");
    await updateDoc(doc(db, "clientes", clientId), {
      archived: false,
      archivedAt: null,
      archivedBy: null,
    });
  }, [user]);

  // Contracts archive from the browser (no money moves); the check that every
  // fianza is already archived runs inside the transaction so a fianza landing
  // at the same moment cannot slip under the archive.
  const archiveContract = useCallback(async (contractId) => {
    if (!user?.uid) throw new Error("User not authenticated");
    return mutateContract(db, contractId, (contrato) => {
      if (contrato.archived === true) return null;
      const vivas = (contrato.fianzas || []).filter((f) => !f?.archivada);
      if (vivas.length > 0) {
        const err = new Error("El contrato tiene fianzas sin archivar.");
        err.code = "CONTRATO_CON_FIANZAS";
        err.fianzas = vivas;
        throw err;
      }
      return { archived: true, archivedAt: Date.now(), archivedBy: user.uid };
    });
  }, [user]);

  const unarchiveContract = useCallback(async (contractId) => {
    if (!user?.uid) throw new Error("User not authenticated");
    await updateDoc(doc(db, "contracts", contractId), {
      archived: false,
      archivedAt: null,
      archivedBy: null,
    });
  }, [user]);

  const fetchArchivedContracts = useCallback(async () => {
    if (!userData?.org) return [];
    const snap = await getDocs(
      query(
        collection(db, "contracts"),
        where("org", "==", userData.org),
        where("archived", "==", true)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }, [userData?.org]);

  // Contracts that still hold at least one archived fianza (the backend keeps
  // `tieneFianzasArchivadas` in step). The caller reads fianzas[] unfiltered.
  const fetchContractsWithArchivedFianzas = useCallback(async () => {
    if (!userData?.org) return [];
    const snap = await getDocs(
      query(
        collection(db, "contracts"),
        where("org", "==", userData.org),
        where("tieneFianzasArchivadas", "==", true)
      )
    );
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((c) => c.archived !== true);
  }, [userData?.org]);

  const fetchArchivedClients = useCallback(async () => {
    if (!user?.uid) return [];
    let q;
    if (userData?.org) {
      q = query(
        collection(db, "clientes"),
        where("org", "==", userData.org),
        where("archived", "==", true),
        orderBy("nombreEmpresa")
      );
    } else {
      q = query(
        collection(db, "clientes"),
        where("usuario", "array-contains", user.uid),
        where("archived", "==", true),
        orderBy("nombreEmpresa")
      );
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }, [user, userData?.org]);

  const fetchClientContracts = useCallback(async (contractIds) => {
    if (!contractIds || contractIds.length === 0) return [];
    const chunks = chunkArray(contractIds.filter(Boolean), 10);
    const results = [];
    for (const chunk of chunks) {
      const snap = await getDocs(
        query(collection(db, "contracts"), where(documentId(), "in", chunk))
      );
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
    }
    return results;
  }, []);

  const clientsList = useMemo(() => {
    return Object.values(clientsById).sort((a, b) => {
      if (!a.nombreEmpresa || !b.nombreEmpresa) return 0;
      return a.nombreEmpresa.localeCompare(b.nombreEmpresa, "es");
    });
  }, [clientsById]);

  const contractsList = useMemo(() => Object.values(contractsById), [contractsById]);

  const conveniosList = useMemo(() => {
    return Object.values(conveniosById).sort((a, b) => {
      if (!a.nombre || !b.nombre) return 0;
      return a.nombre.localeCompare(b.nombre, "es");
    });
  }, [conveniosById]);

  const asociadosById = useMemo(() => {
    const m = {};
    asociados.forEach((a) => { m[a.id] = a; });
    return m;
  }, [asociados]);

  const afianzadorasById = useMemo(() => {
    const m = {};
    afianzadorasList.forEach((a) => { m[a.id] = a; });
    return m;
  }, [afianzadorasList]);

  return (
    <DataContext.Provider
      value={{
        clientsById,
        clientsList,
        clientsLoading,
        clientsError,
        contractsById,
        contractsList,
        contractsLoading,
        contractsError,
        addClient,
        updateClient,
        updateClientAssignedUsers,
        updateContractAssignedUsers,
        tarifas,
        tarifasLoading,
        tarifasError,
        comisiones,
        comisionesLoading,
        comisionesError,
        comisionConfig,
        comisionConfigLoading,
        saveComisionConfig,
        asociadosEnabled,
        asociados,
        asociadosById,
        asociadosLoading,
        aprobacionConfig,
        propuestasReparto,
        linksById,
        linksLoading,
        linksError,
        afianzadorasList,
        afianzadorasById,
        afianzadorasLoading,
        conveniosById,
        pendientes,
        pendientesLoading,
        pendientesError,
        otEnabled,
        ordenesTrabajo,
        ordenesTrabajoLoading,
        ordenesTrabajoError,
        conveniosList,
        conveniosLoading,
        conveniosError,
        addConvenio,
        updateConvenio,
        archiveClient,
        unarchiveClient,
        fetchArchivedClients,
        fetchClientContracts,
        archiveContract,
        unarchiveContract,
        fetchArchivedContracts,
        fetchContractsWithArchivedFianzas,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);

export const useClients = () => {
  const {
    clientsById,
    clientsList,
    clientsLoading,
    clientsError,
    addClient,
    updateClient,
    updateClientAssignedUsers,
  } = useContext(DataContext);
  return {
    clientsById,
    clientsList,
    clientsLoading,
    clientsError,
    addClient,
    updateClient,
    updateClientAssignedUsers,
  };
};

export const useContracts = () => {
  const { contractsById, contractsList, contractsLoading, contractsError, updateContractAssignedUsers } = useContext(DataContext);
  return { contractsById, contractsList, contractsLoading, contractsError, updateContractAssignedUsers };
};

export const useClientContracts = (clientId) => {
  const { contractsList } = useContext(DataContext);
  return useMemo(
    () => contractsList.filter((contract) => contract.clientId === clientId),
    [contractsList, clientId]
  );
};

export const useTarifas = () => {
  const { tarifas, tarifasLoading, tarifasError } = useContext(DataContext);
  return { tarifas, tarifasLoading, tarifasError };
};

// The comisión ledger — all org movements from the single live listener above.
// Consumers filter (e.g. by fianzaId) locally. Empty for non-owners.
export const useComisionLedger = () => {
  const { comisiones, comisionesLoading } = useContext(DataContext);
  return { movements: comisiones, loading: comisionesLoading };
};

// Org comisión config (orgs/{org}/config/comisiones): participación de
// convenios per afianzadora. Empty object until loaded / for client users.
export const useComisionConfig = () => {
  const { comisionConfig, comisionConfigLoading, saveComisionConfig } = useContext(DataContext);
  return { config: comisionConfig, loading: comisionConfigLoading, saveComisionConfig };
};

// Asociados y socios of the org plus the Surelia-set module switch. Empty and
// disabled unless orgs.asociadosEnabled. Every reparto surface reads from here.
export const useAsociados = () => {
  const {
    asociadosEnabled, asociados, asociadosById, asociadosLoading, aprobacionConfig, propuestasReparto,
  } = useContext(DataContext);
  return {
    asociadosEnabled: Boolean(asociadosEnabled),
    asociados: asociados || [],
    asociadosById: asociadosById || {},
    loading: Boolean(asociadosLoading),
    // orgs/{org}/config/asociados — owner only; {} otherwise.
    aprobacion: aprobacionConfig || {},
    requiereAprobacion: aprobacionConfig?.requiereAprobacion === true,
    // Pending propuestas de reparto (owner only).
    propuestas: propuestasReparto || [],
  };
};

export const useLinks = () => {
  const { linksById, linksLoading, linksError } = useContext(DataContext);
  return { linksById, linksLoading, linksError };
};

export const useAfianzadoras = () => {
  const { afianzadorasList, afianzadorasById, afianzadorasLoading } = useContext(DataContext);
  return { afianzadorasList, afianzadorasById, afianzadorasLoading };
};

export const useConvenios = () => {
  const { conveniosById, conveniosList, conveniosLoading, conveniosError, addConvenio, updateConvenio } = useContext(DataContext);
  return { conveniosById, conveniosList, conveniosLoading, conveniosError, addConvenio, updateConvenio };
};

/**
 * The org's pendientes, from the single DataProvider listener.
 *
 * Raw docs — decoration and filtering happen in hooks/usePendientes.js. Named
 * *Raw to leave the `usePendientes` name to that module, which is where
 * consumers should go.
 */
export const usePendientesRaw = () => {
  const { pendientes, pendientesLoading, pendientesError } = useContext(DataContext);
  return { pendientes, pendientesLoading, pendientesError };
};

/**
 * The org's órdenes de trabajo, from the single DataProvider listener. Raw
 * docs; decoration lives in hooks/useOrdenesTrabajo.js. Empty with
 * `otEnabled: false` when the add-on is off for the org.
 */
export const useOrdenesTrabajoRaw = () => {
  const {
    otEnabled, ordenesTrabajo, ordenesTrabajoLoading, ordenesTrabajoError,
  } = useContext(DataContext);
  return {
    otEnabled: Boolean(otEnabled),
    ordenesTrabajo: ordenesTrabajo || [],
    ordenesTrabajoLoading: Boolean(ordenesTrabajoLoading),
    ordenesTrabajoError: ordenesTrabajoError || null,
  };
};

export const useConvenioContracts = (convenioId) => {
  const { contractsList } = useContext(DataContext);
  return useMemo(
    () => contractsList.filter((contract) => contract.convenioId === convenioId),
    [contractsList, convenioId]
  );
};

export const useArchive = () => {
  const {
    archiveClient, unarchiveClient, fetchArchivedClients, fetchClientContracts,
    archiveContract, unarchiveContract, fetchArchivedContracts, fetchContractsWithArchivedFianzas,
  } = useContext(DataContext);
  return {
    archiveClient, unarchiveClient, fetchArchivedClients, fetchClientContracts,
    archiveContract, unarchiveContract, fetchArchivedContracts, fetchContractsWithArchivedFianzas,
  };
};
